// miniapp/src/components/common/CloseConfirmModal.tsx
//
// Phase 35 (Pen-test Finding #3, CWE-303) — center-of-screen
// confirmation modal shown when the user taps the X-close icon
// in the app bar. Replaces the native SuperApp
// `closeMiniAppWithExitDialog` dialog because the native flow
// gives JS no callback on Yes/No, so we can't run the JWT
// revocation hook from there. Our JS modal preserves the same
// visual style (centered, rounded card, white background, blue
// primary) and copy ("Are you sure you want to close the
// DGSupport app?") so the experience feels native to users
// who saw the host dialog previously.
//
// On Confirm: parent calls useAuth().logout() (revokes the JWT
// + clears localStorage) AND bridgeService.close() to close
// the miniapp via the SuperApp host.
// On Cancel: dismisses; session stays intact.

import { useEffect, useRef, useState } from 'react';

export interface CloseConfirmModalProps {
  /** Tapped Yes — parent revokes the JWT, clears local state, then closes. */
  onConfirm: () => void;
  /** Tapped Cancel, backdrop, or Escape. */
  onCancel: () => void;
}

const TITLE_ID = 'close-confirm-modal-title';

export function CloseConfirmModal({ onConfirm, onCancel }: CloseConfirmModalProps) {
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Focus management — capture the element that had focus before the
  // modal opened, focus the Cancel button on open (the less-destructive
  // option per WAI-ARIA dialog pattern), and restore focus on close.
  useEffect(() => {
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;
    cancelBtnRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, []);

  // Keyboard: Escape closes; Tab + Shift+Tab cycle between the two
  // buttons so focus can't leak out of the modal while it's open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = [cancelBtnRef.current, confirmBtnRef.current].filter(
        (el): el is HTMLButtonElement => el !== null,
      );
      if (focusables.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (active === first || !focusables.includes(active as HTMLButtonElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !focusables.includes(active as HTMLButtonElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Fade-in + slight scale-up on mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onCancel}
      style={{ touchAction: 'none' }}
    >
      <div
        className="absolute inset-0 bg-black/40"
        style={{
          opacity: mounted ? 1 : 0,
          transition: 'opacity 180ms ease-out',
        }}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="relative mx-6 w-full max-w-sm bg-white rounded-3xl shadow-2xl px-6 pt-6 pb-5"
        onClick={(e) => e.stopPropagation()}
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'scale(1)' : 'scale(0.96)',
          transition:
            'opacity 180ms ease-out, transform 180ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <h2 id={TITLE_ID} className="text-center text-xl font-semibold text-gray-900">
          DGSupport
        </h2>
        <p className="mt-3 text-center text-base text-gray-700 leading-relaxed">
          Are you sure you want to close the DGSupport app?
        </p>

        <div className="mt-6 flex gap-3">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            Cancel
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-xl bg-[#2563EB] px-4 py-3 text-base font-semibold text-white hover:bg-[#1D4ED8] active:bg-[#1E40AF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1D4ED8] focus-visible:ring-offset-2"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}
